package com.evoapps.evoshift.ui.workplaces

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.shift_preset_afternoon
import evoshift.composeapp.generated.resources.shift_preset_middle
import evoshift.composeapp.generated.resources.shift_preset_morning
import evoshift.composeapp.generated.resources.shift_presets_hint
import evoshift.composeapp.generated.resources.shift_presets_title
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** SH-219: a usual shift to start from: its name (in the owner's language) and times. */
data class ShiftTemplatePreset(val name: StringResource, val start: String, val end: String) {
    /** "7–15", "10:30–18": whole hours without the minutes. */
    val times: String get() = "${short(start)}–${short(end)}"

    private fun short(time: String) = if (time.endsWith(":00")) time.substringBefore(':').trimStart('0').ifEmpty { "0" } else time
}

/** SH-219: morning, afternoon and middle shifts of a business open from 7 to 23. */
val ShiftTemplatePresets = listOf(
    ShiftTemplatePreset(Res.string.shift_preset_morning, "07:00", "15:00"),
    ShiftTemplatePreset(Res.string.shift_preset_afternoon, "15:00", "23:00"),
    ShiftTemplatePreset(Res.string.shift_preset_middle, "10:00", "18:00")
)

const val SHIFT_PRESETS_TAG = "shift_template_presets"

/**
 * SH-219: the presets not defined yet (no template with the same times), each added with one tap
 * ("+ Poranna 7–15") and edited afterwards like any other template; nothing once all are there.
 * On the navy sheet of the shift templates.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun ShiftTemplatePresetsSection(existing: List<ShiftTemplate>, onAdd: (name: String, start: String, end: String) -> Unit) {
    val missing = ShiftTemplatePresets.filter { preset -> existing.none { it.startTime == preset.start && it.endTime == preset.end } }
    if (missing.isEmpty()) return
    Column(Modifier.fillMaxWidth().padding(vertical = 8.dp).testTag(SHIFT_PRESETS_TAG)) {
        Text(stringResource(Res.string.shift_presets_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = Color.White)
        Text(stringResource(Res.string.shift_presets_hint), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.85f))
        FlowRow(
            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            missing.forEach { preset ->
                val name = stringResource(preset.name)
                EvoSecondaryButton(DialogAction("$name ${preset.times}", { onAdd(name, preset.start, preset.end) }, icon = Icons.Default.Add))
            }
        }
    }
}
